import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, Plus, Trash2, TriangleAlert } from "lucide-react";
import { rhm, apiError } from "../api";
import { Badge, Btn, Card, Empty, Field, Input, PageHead, Select, Table, Td, Textarea } from "../ui";
import { ListField } from "./websiteFields";
import { useSaveShortcut } from "../useSaveShortcut";

const BASE = process.env.REACT_APP_BACKEND_URL;

const GUIDE = [
    {
        title: "1 · Google Search Console (do this first)",
        steps: [
            "Open search.google.com/search-console and sign in with your Google account.",
            "Click Add property → choose the URL prefix option → enter https://rehealthme.com",
            "Verify by the HTML tag method: Google shows a <meta name=\"google-site-verification\" …> tag. Paste that whole tag into the box below and press SAVE, then click Verify in Google.",
            "Once verified, go to Sitemaps and submit: api/seo/sitemap.xml",
            "Go to URL Inspection, paste https://rehealthme.com, press Request indexing. Repeat for your shop page and top 5 products.",
            "Check Pages → 'Not indexed' after a week; anything listed as 'Soft 404' or 'Crawled, not indexed' should get a redirect rule below.",
        ],
    },
    {
        title: "2 · Google Business Profile (this creates the brand box)",
        steps: [
            "Open business.google.com → Add your business → use the exact name you use publicly.",
            "Pick the category Ayurvedic clinic / Unani practitioner, and add the clinic address, phone and hours.",
            "Add the website as https://rehealthme.com so the profile and the site are linked.",
            "Complete verification (postcard, phone or video, whichever Google offers).",
            "Add 10+ photos, your services, and your practitioner bio. Ask existing patients for reviews — reviews on the profile are what Google trusts.",
        ],
    },
    {
        title: "3 · Everything else that moves the needle",
        steps: [
            "Bing Webmaster Tools (bing.com/webmasters) — import straight from Search Console in one click.",
            "Fill in every social profile with the same name, logo and website link, then paste those URLs in 'Brand links' below so Google can connect them.",
            "Keep your name, phone and address identical everywhere (site, Google profile, Instagram, Practo, JustDial).",
            "Publish under your real name with credentials — the author fields below feed that into every article.",
            "Ask for a mention + link from your clinic's site, any association page, or local press. Even two real links change how Google treats a new brand.",
        ],
    },
];

const Guide = () => (
    <Card title="Step-by-step: getting listed and featured" data-testid="seo-guide">
        <div className="space-y-6">
            {GUIDE.map((block) => (
                <div key={block.title}>
                    <div className="mono text-[10px] tracking-[0.18em] text-gold">{block.title.toUpperCase()}</div>
                    <ol className="mt-3 space-y-2 list-decimal list-inside text-sm text-zinc-400">
                        {block.steps.map((s) => <li key={s} className="leading-relaxed">{s}</li>)}
                    </ol>
                </div>
            ))}
        </div>
    </Card>
);

const PLATFORMS = [
    { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/rehealthme" },
    { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@rehealthme" },
    { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/rehealthme" },
    { key: "x", label: "X (Twitter)", placeholder: "https://x.com/rehealthme" },
    { key: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/company/rehealthme" },
    { key: "whatsapp", label: "WhatsApp Business", placeholder: "https://wa.me/91XXXXXXXXXX" },
    { key: "practo", label: "Practo", placeholder: "https://practo.com/…" },
    { key: "justdial", label: "JustDial", placeholder: "https://jsdl.in/…" },
    { key: "wikipedia", label: "Wikipedia / Wikidata", placeholder: "https://en.wikipedia.org/wiki/…" },
    { key: "google_maps", label: "Google Maps listing", placeholder: "https://maps.app.goo.gl/…" },
];

/** One place for every profile — these feed both the site footer and Google's brand box. */
const BrandLinks = ({ conf, set }) => {
    const profiles = conf.profiles || {};
    const filled = PLATFORMS.filter((p) => profiles[p.key]).length;
    return (
        <div data-testid="brand-links">
            <div className="flex items-center justify-between mb-3">
                <div className="mono text-[10px] tracking-[0.18em] text-zinc-400">
                    BRAND LINKS — {filled}/{PLATFORMS.length} ADDED
                </div>
                <div className="text-[11px] text-zinc-500">Shown in the footer and sent to Google as sameAs</div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                {PLATFORMS.map((p) => (
                    <Field key={p.key} label={p.label}>
                        <Input value={profiles[p.key] || ""} placeholder={p.placeholder}
                            data-testid={`brand-link-${p.key}`}
                            onChange={(e) => set({ profiles: { ...profiles, [p.key]: e.target.value } })} />
                    </Field>
                ))}
            </div>
        </div>
    );
};

const Settings = () => {
    const qc = useQueryClient();
    const { data } = useQuery({ queryKey: ["seo-crm-settings"], queryFn: async () => (await rhm.get("/seo/settings")).data });
    const [form, setForm] = useState(null);
    const conf = form || data;
    const set = (patch) => setForm({ ...(conf || {}), ...patch });
    const setIn = (group, patch) => set({ [group]: { ...((conf || {})[group] || {}), ...patch } });

    const save = useMutation({
        mutationFn: async () => (await rhm.put("/seo/settings", conf)).data,
        onSuccess: () => { toast.success("Brand details saved"); setForm(null); qc.invalidateQueries({ queryKey: ["seo-crm-settings"] }); },
        onError: (e) => toast.error(apiError(e)),
    });
    useSaveShortcut(() => conf && save.mutate(), !!form);

    if (!conf) return <Card title="Brand & author details"><Empty msg="Loading…" /></Card>;
    const founder = conf.founder || {};
    const clinic = conf.clinic || {};

    return (
        <div className="space-y-4">
            <Card title="Brand identity (feeds Google's brand box)" action={
                <Btn onClick={() => save.mutate()} disabled={!form || save.isPending} data-testid="seo-save">
                    {save.isPending ? "SAVING…" : "SAVE"}
                </Btn>}>
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Brand name"><Input value={conf.brand_name || ""} data-testid="seo-brand-name"
                        onChange={(e) => set({ brand_name: e.target.value })} /></Field>
                    <Field label="Registered / legal name"><Input value={conf.legal_name || ""} data-testid="seo-legal-name"
                        onChange={(e) => set({ legal_name: e.target.value })} /></Field>
                    <Field label="Website address" hint="Used for the sitemap and canonical links">
                        <Input value={conf.site_url || ""} data-testid="seo-site-url"
                            onChange={(e) => set({ site_url: e.target.value })} /></Field>
                    <Field label="Tagline"><Input value={conf.tagline || ""} data-testid="seo-tagline"
                        onChange={(e) => set({ tagline: e.target.value })} /></Field>
                    <Field label="Logo URL (square works best)"><Input value={conf.logo_url || ""} data-testid="seo-logo"
                        onChange={(e) => set({ logo_url: e.target.value })} /></Field>
                    <Field label="Site icon / favicon"
                        hint="Square PNG — upload in Media Library and paste the URL. Leave blank for the built-in icon.">
                        <Input value={conf.favicon_url || ""} data-testid="seo-favicon"
                            onChange={(e) => set({ favicon_url: e.target.value })} /></Field>
                    <Field label="Default share image"><Input value={conf.social_image || ""} data-testid="seo-social-image"
                        onChange={(e) => set({ social_image: e.target.value })} /></Field>
                    <Field label="Brand description" className="md:col-span-3">
                        <Textarea rows={2} value={conf.description || ""} data-testid="seo-description"
                            onChange={(e) => set({ description: e.target.value })} /></Field>
                    <Field label="Google site verification tag" className="md:col-span-3"
                        hint="Paste the whole <meta …> tag from Search Console">
                        <Input value={conf.verification_tag || ""} data-testid="seo-verification"
                            onChange={(e) => set({ verification_tag: e.target.value })} /></Field>
                    <div className="md:col-span-3">
                        <BrandLinks conf={conf} set={set} />
                    </div>
                    <div className="md:col-span-3">
                        <ListField field={{ key: "same_as", label: "Any other profile links" }}
                            value={conf.same_as || []} onChange={(v) => set({ same_as: v })} />
                    </div>
                </div>
            </Card>

            <Card title="Author & medical reviewer (health pages need a real person)">
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Name"><Input value={founder.name || ""} data-testid="seo-founder-name"
                        onChange={(e) => setIn("founder", { name: e.target.value })} /></Field>
                    <Field label="Role / title"><Input value={founder.job_title || ""} data-testid="seo-founder-role"
                        onChange={(e) => setIn("founder", { job_title: e.target.value })} /></Field>
                    <Field label="Qualifications"><Input value={founder.credentials || ""} data-testid="seo-founder-credentials"
                        onChange={(e) => setIn("founder", { credentials: e.target.value })} /></Field>
                    <Field label="Short bio" className="md:col-span-2">
                        <Textarea rows={2} value={founder.bio || ""} data-testid="seo-founder-bio"
                            onChange={(e) => setIn("founder", { bio: e.target.value })} /></Field>
                    <Field label="Profile page"><Input value={founder.profile_url || ""} data-testid="seo-founder-url"
                        onChange={(e) => setIn("founder", { profile_url: e.target.value })} /></Field>
                    <Field label="Reviewer note shown on articles" className="md:col-span-3">
                        <Input value={conf.reviewer_note || ""} data-testid="seo-reviewer-note"
                            onChange={(e) => set({ reviewer_note: e.target.value })} /></Field>
                </div>
            </Card>

            <Card title="Clinic & trust line">
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Clinic name"><Input value={clinic.name || ""} data-testid="seo-clinic-name"
                        onChange={(e) => setIn("clinic", { name: e.target.value })} /></Field>
                    <Field label="Street"><Input value={clinic.street || ""} data-testid="seo-clinic-street"
                        onChange={(e) => setIn("clinic", { street: e.target.value })} /></Field>
                    <Field label="City"><Input value={clinic.city || ""} data-testid="seo-clinic-city"
                        onChange={(e) => setIn("clinic", { city: e.target.value })} /></Field>
                    <Field label="State"><Input value={clinic.region || ""} data-testid="seo-clinic-region"
                        onChange={(e) => setIn("clinic", { region: e.target.value })} /></Field>
                    <Field label="PIN code"><Input value={clinic.postal_code || ""} data-testid="seo-clinic-pin"
                        onChange={(e) => setIn("clinic", { postal_code: e.target.value })} /></Field>
                    <Field label="Phone"><Input value={clinic.phone || ""} data-testid="seo-clinic-phone"
                        onChange={(e) => setIn("clinic", { phone: e.target.value })} /></Field>
                    <Field label="Google profile link"><Input value={clinic.google_profile_url || ""} data-testid="seo-clinic-gbp"
                        onChange={(e) => setIn("clinic", { google_profile_url: e.target.value })} /></Field>
                    <Field label="Google rating to display"><Input value={clinic.rating || ""} data-testid="seo-clinic-rating"
                        onChange={(e) => setIn("clinic", { rating: e.target.value })} /></Field>
                    <Field label="Show the rating on the site"
                        hint="Shown as a fact with a link to your Google profile — never as a fake product rating">
                        <Select value={conf.show_rating === false ? "no" : "yes"} data-testid="seo-show-rating"
                            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                            onChange={(e) => set({ show_rating: e.target.value === "yes" })} /></Field>
                    <Field label="Trust line" className="md:col-span-3">
                        <Input value={conf.trust_line || ""} data-testid="seo-trust-line"
                            onChange={(e) => set({ trust_line: e.target.value })} /></Field>
                </div>
            </Card>
        </div>
    );
};

const Redirects = () => {
    const qc = useQueryClient();
    const { data } = useQuery({ queryKey: ["seo-redirects"], queryFn: async () => (await rhm.get("/seo/redirects")).data });
    const [row, setRow] = useState({ from_path: "", to_path: "", note: "" });

    const add = useMutation({
        mutationFn: async () => (await rhm.post("/seo/redirects", row)).data,
        onSuccess: () => { toast.success("Redirect added"); setRow({ from_path: "", to_path: "", note: "" }); qc.invalidateQueries({ queryKey: ["seo-redirects"] }); },
        onError: (e) => toast.error(apiError(e)),
    });
    const remove = useMutation({
        mutationFn: async (id) => (await rhm.delete(`/seo/redirects/${id}`)).data,
        onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["seo-redirects"] }); },
        onError: (e) => toast.error(apiError(e)),
    });

    const rules = data?.rules || [];
    const builtin = data?.builtin || [];

    return (
        <div className="space-y-4">
            <Card title="Send old links to their new home">
                <div className="grid md:grid-cols-4 gap-3 items-end">
                    <Field label="Old address"><Input value={row.from_path} placeholder="/old-page" data-testid="redirect-from"
                        onChange={(e) => setRow({ ...row, from_path: e.target.value })} /></Field>
                    <Field label="New address"><Input value={row.to_path} placeholder="/shop" data-testid="redirect-to"
                        onChange={(e) => setRow({ ...row, to_path: e.target.value })} /></Field>
                    <Field label="Note (optional)"><Input value={row.note} data-testid="redirect-note"
                        onChange={(e) => setRow({ ...row, note: e.target.value })} /></Field>
                    <Btn onClick={() => add.mutate()} disabled={add.isPending} data-testid="redirect-add">
                        <Plus className="w-3.5 h-3.5 mr-1 inline" /> ADD
                    </Btn>
                </div>
                <Table head={["OLD", "NEW", "NOTE", ""]} empty="No custom rules yet" testId="redirect-table">
                    {rules.map((r) => (
                        <tr key={r.id} className="border-b border-white/5" data-testid={`redirect-row-${r.id}`}>
                            <Td className="mono text-[11px]">{r.from_path}</Td>
                            <Td className="mono text-[11px] text-lime">{r.to_path}</Td>
                            <Td className="text-zinc-500">{r.note || "—"}</Td>
                            <Td>
                                <button onClick={() => remove.mutate(r.id)} data-testid={`redirect-remove-${r.id}`}
                                    className="text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                            </Td>
                        </tr>
                    ))}
                </Table>
            </Card>

            <Card title="Already handled automatically">
                <div className="space-y-2 text-sm text-zinc-400">
                    {builtin.map((b) => (
                        <div key={b.id} className="flex items-center gap-3" data-testid={`builtin-${b.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 text-lime shrink-0" />
                            <span className="mono text-[11px]">{b.from_path}</span>
                            <span className="text-zinc-600">→</span>
                            <span className="mono text-[11px] text-lime">{b.to_path}</span>
                        </div>
                    ))}
                    <p className="text-xs text-zinc-500 pt-2">
                        Old WordPress blog links like /2026/07/02/l-arginine-benefits-men/ land on the matching
                        /blog/&lt;slug&gt; article; anything with no match returns a page that is kept out of Google.
                    </p>
                </div>
            </Card>
        </div>
    );
};

const Health = () => {
    const { data } = useQuery({ queryKey: ["seo-health"], queryFn: async () => (await rhm.get("/seo/health")).data });
    const { data: sm } = useQuery({ queryKey: ["seo-sitemap"], queryFn: async () => (await rhm.get("/seo/sitemap-urls")).data });
    const rows = data?.rows || [];

    return (
        <div className="space-y-4">
            <Card title="Sitemap" action={
                <a href={`${BASE}/api/seo/sitemap.xml`} target="_blank" rel="noreferrer" data-testid="sitemap-link"
                    className="mono text-[10px] tracking-[0.18em] text-gold inline-flex items-center gap-1.5">
                    OPEN SITEMAP <ExternalLink className="w-3 h-3" />
                </a>}>
                <div className="text-sm text-zinc-400" data-testid="sitemap-summary">
                    {sm ? `${sm.count} addresses listed, built live from your pages, products, collections, science pages and articles.` : "Loading…"}
                </div>
                <div className="mt-3 mono text-[11px] text-zinc-500">
                    Submit in Search Console as: <span className="text-lime">api/seo/sitemap.xml</span>
                </div>
            </Card>

            <Card title={`Titles & descriptions${data ? ` — ${data.clean}/${data.total} clean` : ""}`}>
                <Table head={["TYPE", "PAGE", "TITLE", "ISSUES"]} empty="Nothing to check yet" testId="seo-health-table">
                    {rows.map((r) => (
                        <tr key={`${r.kind}-${r.path}`} className="border-b border-white/5"
                            data-testid={`seo-health-row-${r.path}`}>
                            <Td><Badge tone="muted">{r.kind}</Badge></Td>
                            <Td>
                                <div className="text-sm">{r.name}</div>
                                <div className="mono text-[10px] text-zinc-500">{r.path}</div>
                            </Td>
                            <Td className="max-w-[320px]">
                                <div className="text-xs text-zinc-300 truncate">{r.title || "—"}</div>
                                <div className="text-[11px] text-zinc-500 line-clamp-2">{r.description || "No description"}</div>
                            </Td>
                            <Td>
                                {r.noindex ? <Badge tone="warn">Hidden from Google</Badge>
                                    : r.issues.length === 0 ? <Badge tone="good">Good</Badge>
                                        : (
                                            <div className="space-y-1">
                                                {r.issues.map((i) => (
                                                    <div key={i} className="flex items-center gap-1.5 text-[11px] text-gold">
                                                        <TriangleAlert className="w-3 h-3" /> {i}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                            </Td>
                        </tr>
                    ))}
                </Table>
            </Card>
        </div>
    );
};

const TABS = [
    { key: "guide", label: "GET LISTED", node: <Guide /> },
    { key: "brand", label: "BRAND & AUTHOR", node: <Settings /> },
    { key: "health", label: "SITEMAP & CHECKS", node: <Health /> },
    { key: "redirects", label: "OLD LINKS", node: <Redirects /> },
];

export default function RhmSeo() {
    const [tab, setTab] = useState("guide");
    return (
        <div className="space-y-5" data-testid="rhm-seo-page">
            <PageHead title="SEO & Google" sub="How ReHealthMe appears in search, and everything Google needs from you" />
            <div className="flex flex-wrap gap-1 border-b border-white/10">
                {TABS.map((t) => (
                    <button key={t.key} onClick={() => setTab(t.key)} data-testid={`seo-tab-${t.key}`}
                        className={`px-4 py-3 mono text-[10px] tracking-[0.18em] border-b-2 -mb-px transition-colors ${
                            tab === t.key ? "border-gold text-gold" : "border-transparent text-zinc-500 hover:text-white"}`}>
                        {t.label}
                    </button>
                ))}
            </div>
            {TABS.find((t) => t.key === tab)?.node}
        </div>
    );
}
